import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, Avatar,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { Veiculo } from '../../types/frota';
import frotaService from '../../services/frotaService';
import EscritorioSelect from '../selects/EscritorioSelect';
import { toast } from 'react-toastify';

interface VeiculoFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  veiculo?: Veiculo | null;
}

const VeiculoForm: React.FC<VeiculoFormProps> = ({ open, onClose, onSuccess, veiculo }) => {
  const [loading, setLoading] = useState(false);
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [form, setForm] = useState({
    placa: '', marca: '', modelo: '', ano: '', cor: '',
    kilometragem_atual: 0, media_consumo_km_litro: '' as string | number,
    escritorio_id: null as number | null, status: 'ativo' as Veiculo['status'], observacoes: '',
  });

  useEffect(() => {
    if (veiculo) {
      setForm({
        placa: veiculo.placa, marca: veiculo.marca, modelo: veiculo.modelo,
        ano: veiculo.ano?.toString() || '', cor: veiculo.cor || '',
        kilometragem_atual: veiculo.kilometragem_atual,
        media_consumo_km_litro: veiculo.media_consumo_km_litro?.toString() || '',
        escritorio_id: veiculo.escritorio_id,
        status: veiculo.status, observacoes: veiculo.observacoes || '',
      });
      setFoto(null);
      setFotoPreview(veiculo.foto_url ? frotaService.getUploadUrl(veiculo.foto_url) : null);
    } else {
      setForm({
        placa: '', marca: '', modelo: '', ano: '', cor: '',
        kilometragem_atual: 0, media_consumo_km_litro: '',
        escritorio_id: null,
        status: 'ativo', observacoes: '',
      });
      setFoto(null);
      setFotoPreview(null);
    }
  }, [veiculo, open]);

  const handlePlacaChange = (value: string) => {
    setForm({ ...form, placa: value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7) });
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFoto(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('placa', form.placa.toUpperCase());
    fd.append('marca', form.marca);
    fd.append('modelo', form.modelo);
    if (form.ano) fd.append('ano', form.ano);
    if (form.cor) fd.append('cor', form.cor);
    fd.append('kilometragem_atual', String(form.kilometragem_atual));
    if (form.media_consumo_km_litro !== '') {
      fd.append('media_consumo_km_litro', String(form.media_consumo_km_litro));
    }
    fd.append('escritorio_id', String(form.escritorio_id));
    fd.append('status', form.status);
    if (form.observacoes) fd.append('observacoes', form.observacoes);
    if (foto) fd.append('foto', foto);
    return fd;
  };

  const handleSubmit = async () => {
    if (!form.placa || !form.marca || !form.modelo || !form.escritorio_id) {
      toast.error('Preencha placa, marca, modelo e escritório');
      return;
    }
    if (form.placa.length > 7) {
      toast.error('A placa deve ter no máximo 7 caracteres');
      return;
    }
    try {
      setLoading(true);
      const fd = buildFormData();
      if (veiculo) {
        await frotaService.atualizarVeiculo(veiculo.id, fd);
        toast.success('Veículo atualizado');
      } else {
        await frotaService.criarVeiculo(fd);
        toast.success('Veículo cadastrado');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao salvar veículo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{veiculo ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                src={fotoPreview || undefined}
                variant="rounded"
                sx={{ width: 120, height: 90, bgcolor: 'grey.200' }}
              >
                {!fotoPreview && <PhotoCamera color="disabled" />}
              </Avatar>
              <Box>
                <Button component="label" variant="outlined" startIcon={<PhotoCamera />} size="small">
                  {fotoPreview ? 'Trocar foto' : 'Anexar foto do veículo'}
                  <input type="file" hidden accept="image/*" onChange={handleFotoChange} />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  JPG, PNG ou WEBP — máx. 10 MB
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth label="Placa" value={form.placa}
              onChange={(e) => handlePlacaChange(e.target.value)}
              inputProps={{ maxLength: 7 }}
              helperText="Máximo 7 caracteres"
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Marca" value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Modelo" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} required />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Ano" type="number" value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="Cor" value={form.cor} onChange={(e) => setForm({ ...form, cor: e.target.value })} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" label="KM Atual" type="number" value={form.kilometragem_atual} onChange={(e) => setForm({ ...form, kilometragem_atual: Number(e.target.value) })} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Média de consumo (Km/L)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={form.media_consumo_km_litro}
              onChange={(e) => setForm({ ...form, media_consumo_km_litro: e.target.value })}
              helperText="Ex: 12.5"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select size="small" value={form.status} label="Status" onChange={(e) => setForm({ ...form, status: e.target.value as Veiculo['status'] })}>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
                <MenuItem value="manutencao">Em manutenção</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <EscritorioSelect label="Escritório" value={form.escritorio_id} onChange={(v) => setForm({ ...form, escritorio_id: v as number })} required />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline rows={2} label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VeiculoForm;
